import React from 'react';
import {
	TableRow,
	TableCell,
	Typography,
	IconButton,
	TextField,
	Select,
	MenuItem,
	Button,
	Box,
	InputAdornment,
	makeStyles,
} from '@material-ui/core';
import {Add as AddIcon, Remove as RemoveIcon, Delete as DeleteIcon} from '@material-ui/icons';
import {Link} from 'react-router-dom';
import {getStatusColor, ORDER_STATUS_OPTIONS, CANCEL_REASONS} from '../constants/orderConstants';
import {formatVatRate} from '../utils/vat';

const useStyles = makeStyles((theme) => ({
	cancelledRow: {
		backgroundColor: '#f5f5f5',
		// Apply opacity and line-through to all cells except the last one (actions column)
		'& td:not(:last-child)': {
			opacity: 0.7,
			textDecoration: 'line-through',
		},
		// Keep buttons in the action column fully visible
		'& button': {
			opacity: 1,
			textDecoration: 'none',
		},
	},
	quantityControl: {
		display: 'flex',
		alignItems: 'center',
		border: '1px solid #ddd',
		borderRadius: 4,
		width: 'fit-content',
	},
	quantityText: {
		margin: '0 8px',
		minWidth: 30,
		textAlign: 'center',
	},
	priceInput: {
		width: 90,
	},
	replaceButton: {
		backgroundColor: '#8bc34a',
		color: '#fff',
		minWidth: 70,
		marginRight: 8,
		'&:hover': {
			backgroundColor: '#7cb342',
		},
	},
	cancelButton: {
		backgroundColor: '#f44336',
		color: '#fff',
		minWidth: 70,
		'&:hover': {
			backgroundColor: '#d32f2f',
		},
	},
	restoreButton: {
		backgroundColor: '#2196f3',
		color: '#fff',
		minWidth: 70,
		'&:hover': {
			backgroundColor: '#1976d2',
		},
	},
	actionButtons: {
		display: 'flex',
		gap: 4,
	},
	deleteButton: {
		color: '#f44336',
		padding: 8,
		'&:hover': {
			backgroundColor: '#ffebee',
		},
	},
}));

const QuantityControl = ({quantity, onIncrement, onDecrement, disabled, error}) => {
	const classes = useStyles();

	return (
		<Box>
			<Box className={classes.quantityControl} style={{borderColor: error ? '#f44336' : '#ddd'}}>
				<IconButton size="small" onClick={onDecrement} disabled={disabled}>
					<RemoveIcon fontSize="small"/>
				</IconButton>
				<Typography className={classes.quantityText}>{quantity}</Typography>
				<IconButton size="small" onClick={onIncrement} disabled={disabled}>
					<AddIcon fontSize="small"/>
				</IconButton>
			</Box>
			{error && (
				<Typography variant="caption" style={{color: '#f44336', display: 'block', marginTop: 4}}>
					{error}
				</Typography>
			)}
		</Box>
	);
};

const PriceField = ({value, onChange, disabled, className, error, allowEmpty = false}) => {
	// If allowEmpty is true and value is 0/null/undefined, show empty string
	// Otherwise ensure value is a number
	const displayValue = allowEmpty && (!value || parseFloat(value) === 0) ? '' : (parseFloat(value) || 0);

	return (
		<Box>
			<TextField
				size="small"
				type="number"
				value={displayValue}
				onChange={(e) => onChange(e.target.value)}
				disabled={disabled}
				error={!!error}
				inputProps={{min: 0, step: 0.01}}
				InputProps={{
					startAdornment: <InputAdornment position="start">$</InputAdornment>,
				}}
				className={className}
			/>
			{error && (
				<Typography variant="caption" style={{color: '#f44336', display: 'block', marginTop: 4}}>
					{error}
				</Typography>
			)}
		</Box>
	);
};

const StatusSelect = ({status, onChange, disabled, error}) => (
	<Box>
		<Select
			value={status}
			onChange={(e) => onChange(e.target.value)}
			size="small"
			disabled={disabled}
			error={!!error}
			style={{
				padding: '4px 8px',
				fontSize: '0.85rem',
				backgroundColor: getStatusColor(status),
				color: 'white',
				borderRadius: 4,
				minWidth: 90,
				border: error ? '1px solid #f44336' : 'none',
			}}
		>
			{ORDER_STATUS_OPTIONS.map((option) => (
				<MenuItem key={option.value} value={option.value}>
					{option.label}
				</MenuItem>
			))}
		</Select>
		{error && (
			<Typography variant="caption" style={{color: '#f44336', display: 'block', marginTop: 4}}>
				{error}
			</Typography>
		)}
	</Box>
);

const CancelReasonSelect = ({value, onChange, show, error}) => {
	const useStyles = makeStyles((theme) => ({
		reasonChip: {
			backgroundColor: '#ffebee',
			color: '#c62828',
			padding: '4px 8px',
			borderRadius: 4,
			fontSize: '0.85rem',
			display: 'inline-block',
			marginBottom: 4,
		},
	}));
	const classes = useStyles();

	if (!show) return null;

	return (
		<Box>
			<Select
				value={value}
				onChange={(e) => onChange(e.target.value)}
				size="small"
				displayEmpty
				fullWidth
				error={!!error}
				style={{minWidth: 150, marginBottom: 8, borderColor: error ? '#f44336' : undefined}}
			>
				<MenuItem value="">Select reason</MenuItem>
				{CANCEL_REASONS.map((reason) => (
					<MenuItem key={reason} value={reason}>
						{reason}
					</MenuItem>
				))}
			</Select>
			{error && (
				<Typography variant="caption" style={{color: '#f44336', display: 'block', marginBottom: 4}}>
					{error}
				</Typography>
			)}
			{/*{value && (*/}
			{/*	<Box className={classes.reasonChip}>*/}
			{/*		<Typography variant="caption">*/}
			{/*			<strong>Reason:</strong> {value}*/}
			{/*		</Typography>*/}
			{/*	</Box>*/}
			{/*)}*/}
		</Box>
	);
};

const ActionButtons = ({onReplace, onCancel, onRestore, onDelete, disabled, isCancelled, isReplacement, isNewItem}) => {
	const classes = useStyles();

	// If item is cancelled (and not a new item), show restore button
	if (isCancelled && !isNewItem) {
		return (
			<Box className={classes.actionButtons}>
				<Button
					variant="contained"
					size="small"
					onClick={onRestore}
					className={classes.restoreButton}
				>
					Restore
				</Button>
			</Box>
		);
	}

	// For new items, show only delete button
	if (isNewItem) {
		return (
			<Box className={classes.actionButtons}>
				<IconButton
					size="small"
					onClick={onDelete}
					className={classes.deleteButton}
					title="Remove item"
				>
					<DeleteIcon fontSize="small"/>
				</IconButton>
			</Box>
		);
	}

	// Show replace and cancel buttons (+ delete icon if replacement)
	return (
		<Box className={classes.actionButtons}>
			<Button
				variant="contained"
				size="small"
				onClick={onReplace}
				disabled={disabled}
				className={classes.replaceButton}
			>
				Replace
			</Button>
			<Button
				variant="contained"
				size="small"
				onClick={onCancel}
				disabled={disabled}
				className={classes.cancelButton}
			>
				Cancel
			</Button>
			{isReplacement && (
				<IconButton
					size="small"
					onClick={onDelete}
					className={classes.deleteButton}
					title="Delete replacement item"
				>
					<DeleteIcon fontSize="small"/>
				</IconButton>
			)}
		</Box>
	);
};

const OrderItemRow = ({order, storeItem, calculateItemTotal, handlers, getFieldError, showUnitPrice = true}) => {
	const classes = useStyles();
	const isCancelled = order.status === 'cancelled';
	const isReplacement = order.replaced_from !== null;
	const isNewItem = order.isNew === true;

	const {
		handleQuantityChange,
		handleStatusChange,
		handleCancelReasonChange,
		handleCommentChange,
		handlePriceChange,
		handleReplaceItem,
		handleCancelItem,
		handleRestoreItem,
		handleDeleteReplacement,
	} = handlers;

	// Get validation errors for this item
	const quantityError = getFieldError ? getFieldError(order.id, 'quantity') : '';
	const priceError = getFieldError ? getFieldError(order.id, 'item_price') : '';
	const discountError = getFieldError ? getFieldError(order.id, 'coupon_discount_amount') : '';
	const statusError = getFieldError ? getFieldError(order.id, 'status') : '';
	const cancelReasonError = getFieldError ? getFieldError(order.id, 'cancelReason') : '';

	return (
		<React.Fragment>
			<TableRow
				className={isCancelled ? classes.cancelledRow : ''}
				hover={!isCancelled}
				data-item-id={order.id}
			>
				{/* SKU */}
				<TableCell>
					<Box style={{paddingLeft: isReplacement ? '24px' : '0'}}>
						{isNewItem ? (
							<Typography style={{color: '#4caf50', fontStyle: 'italic'}}>
								{order.product_code || 'NEW'}
							</Typography>
						) : (
							<Link
								to={{
									pathname: '/admin/products',
									search: `?sku=${storeItem(order).product_code}`,
								}}
								style={{color: '#007bff', textDecoration: 'underline'}}
							>
								{storeItem(order).product_code}
							</Link>
						)}
					</Box>
				</TableCell>

				{/* Item Name */}
				<TableCell>
					<Box style={{paddingLeft: isReplacement ? '24px' : '0'}}>
						{`${order.item_name}`}
					</Box>
				</TableCell>

				{/* Quantity */}
				<TableCell>
					<QuantityControl
						quantity={order.quantity}
						onIncrement={() => handleQuantityChange(order.id, 1)}
						onDecrement={() => handleQuantityChange(order.id, -1)}
						disabled={isCancelled}
						error={quantityError}
					/>
				</TableCell>

				{/* Weight */}
				<TableCell>{order.item_price_name}</TableCell>

				{/* Unit Price - Only show if any item has original_price > 0 */}
				<TableCell>
					<Box style={{display: 'flex', alignItems: 'center', gap: 8}}>
						{/* Editable item price - always show the field but it's required */}
						<PriceField
							value={order.item_price || 0}
							onChange={(value) => handlePriceChange(order.id, 'item_price', value)}
							disabled={isCancelled}
							className={classes.priceInput}
							error={priceError}
						/>
						{/* Show original price with strikethrough ONLY if item is on promo (original_price > item_price) */}
						{(() => {
							const originalPrice = parseFloat(order.original_price);
							const itemPrice = parseFloat(order.item_price);
							// Only show if original_price is valid (> 0) AND item is on promo (item_price < original_price)
							if (originalPrice && originalPrice > 0 && itemPrice < originalPrice) {
								return (
									<Typography
										style={{
											textDecoration: 'line-through',
											color: '#999',
											fontSize: '1rem',
										}}
									>
										${originalPrice.toFixed(2)}
									</Typography>
								);
							}
							return null;
						})()}
					</Box>
				</TableCell>

				{/* Coupon Discount */}
				<TableCell>
					{order.original_price && parseFloat(order.item_price) < parseFloat(order.original_price) ? (
						// Promo item - show disabled field with 0 and "Promo items excluded" message
						<>
							<PriceField
								value={0}
								onChange={(value) => handlePriceChange(order.id, 'coupon_discount_amount', value)}
								disabled={true}
								className={classes.priceInput}
								error={discountError}
							/>
							<Typography variant="caption"
							            style={{color: '#999', fontSize: '0.75rem', display: 'block', marginTop: 4}}>
								Promo items excluded
							</Typography>
						</>
					) : (
						// Normal item - show discount field (empty if no discount)
						<PriceField
							value={order.coupon_discount_amount || 0}
							onChange={(value) => handlePriceChange(order.id, 'coupon_discount_amount', value)}
							disabled={isCancelled}
							className={classes.priceInput}
							error={discountError}
							allowEmpty={true}
						/>
					)}
				</TableCell>

				{/* Total Price */}
				<TableCell>
					<Box>
						{order.coupon_discount_amount > 0 ? (
							<>
								<Typography style={{textDecoration: 'line-through', color: '#999', fontSize: '0.85rem'}}>
									$ {calculateItemTotal(order).toFixed(2)}
								</Typography>
								<Typography style={{fontWeight: 'bold', color: '#4caf50'}}>
									$ {(calculateItemTotal(order) - (order.coupon_discount_amount || 0)).toFixed(2)}
								</Typography>
							</>
						) : (
							<Typography style={{fontWeight: 'bold'}}>
								$ {calculateItemTotal(order).toFixed(2)}
							</Typography>
						)}
					</Box>
				</TableCell>

				<TableCell>
					{formatVatRate(order.vat_rate)}
				</TableCell>

				{/* DeliPoints Earned */}
				<TableCell>
					{order.dp_points_earned ?? '-'}
				</TableCell>

				{/* DeliPoints Redeemed */}
				<TableCell>
					{order.dp_points_redeemed ?? '-'}
				</TableCell>

				{/* DeliPoints Multiplier */}
				<TableCell>
					{order.dp_product_multiplier ?? '-'}
				</TableCell>

				{/* DeliPoints Earn Promote */}
				<TableCell>
					{order.dp_fix_points_promo_item ?? '-'}
				</TableCell>

				{/* Status */}
				<TableCell>
					<StatusSelect
						status={order.status}
						onChange={(status) => handleStatusChange(order.id, status)}
						disabled={isCancelled}
						error={statusError}
					/>
				</TableCell>

				{/* Cancel Reason */}
				<TableCell>
					<CancelReasonSelect
						value={order.cancelReason}
						onChange={(reason) => handleCancelReasonChange(order.id, reason)}
						show={order.status === 'cancelled'}
						error={cancelReasonError}
					/>
				</TableCell>

				{/* Comment */}
				<TableCell>
					<TextField
						size="small"
						fullWidth
						multiline
						rows={1}
						value={order.comment || ''}
						onChange={(e) => handleCommentChange(order.id, e.target.value)}
						placeholder="Add comment..."
						style={{minWidth: 150}}
					/>
				</TableCell>

				{/* Actions */}
				<TableCell>
					<ActionButtons
						onReplace={() => handleReplaceItem(order.id)}
						onCancel={() => handleCancelItem(order.id)}
						onRestore={() => handleRestoreItem(order.id)}
						onDelete={() => handleDeleteReplacement(order.id)}
						disabled={isCancelled}
						isCancelled={isCancelled}
						isReplacement={isReplacement}
						isNewItem={isNewItem}
					/>
				</TableCell>
			</TableRow>
		</React.Fragment>
	);
};

export default React.memo(OrderItemRow);
